import { motion } from "framer-motion";
import { ArrowDownRight, MoveRight } from "lucide-react";
import { useVariant } from "../../context/VariantContext";
import { HERO, IMAGES } from "../../data/content";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { variant } = useVariant();
  const isVault = variant === "vault";
  const isSwiss = variant === "swiss";

  return (
    <section
      id="top"
      data-testid="hero"
      className={`relative min-h-screen overflow-hidden ${isVault ? "grain" : ""}`}
    >
      {/* Backdrop */}
      {variant === "editorial" && (
        <img
          src={IMAGES.stone}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]"
        />
      )}
      {isSwiss && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
            opacity: 0.08,
          }}
        />
      )}

      <div className="relative mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-16 pt-32 lg:grid-cols-12 lg:px-10">
        {/* Text column */}
        <div className={`lg:col-span-7 ${isSwiss ? "lg:pr-6" : ""}`}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className={`font-accent mb-6 text-[11px] uppercase tracking-[0.3em] ${
              variant === "editorial" ? "italic" : ""
            }`}
            style={{ color: "var(--accent)" }}
          >
            {HERO.eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.05, ease }}
            className={`font-head text-[color:var(--text)] ${
              isSwiss
                ? "text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-8xl"
                : "text-5xl leading-[0.95] sm:text-6xl lg:text-8xl"
            }`}
            style={{ whiteSpace: "pre-line" }}
          >
            {HERO.name}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[color:var(--text-2)] sm:text-lg"
          >
            {HERO.statement}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 border px-7 py-4 text-xs font-medium uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5"
              style={{
                background: "var(--accent)",
                color: "var(--on-accent)",
                borderColor: "var(--accent)",
                borderRadius: "var(--radius-tok)",
              }}
            >
              Request Consultation
              <MoveRight size={15} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 border px-7 py-4 text-xs font-medium uppercase tracking-[0.2em] text-[color:var(--text)] transition-colors hover:bg-[var(--surface)]"
              style={{ borderColor: "var(--border)", borderRadius: "var(--radius-tok)" }}
            >
              Explore Services
              <ArrowDownRight size={15} />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="font-accent mt-12 text-[11px] uppercase tracking-[0.25em] text-[color:var(--text-2)]"
          >
            {HERO.location}
          </motion.p>
        </div>

        {/* Portrait column */}
        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease }}
          className="relative lg:col-span-5"
        >
          <div
            className="relative overflow-hidden border"
            style={{ borderColor: "var(--border)", borderRadius: "var(--radius-tok)" }}
          >
            <img
              src={IMAGES.portraitPrimary}
              alt="Graham Wetzbarger, luxury authentication expert"
              className="aspect-[4/5] w-full object-cover"
              style={{ objectPosition: "center 22%" }}
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background: isVault
                  ? "linear-gradient(180deg, transparent 45%, rgba(10,10,10,0.7) 100%)"
                  : "linear-gradient(180deg, transparent 60%, color-mix(in srgb, var(--accent) 22%, transparent) 100%)",
              }}
            />
          </div>
          <div
            className="absolute -bottom-5 -left-5 hidden border px-5 py-4 sm:block"
            style={{
              background: "var(--surface)",
              borderColor: "var(--border)",
              borderRadius: "var(--radius-tok)",
            }}
          >
            <p className="font-head text-2xl text-[color:var(--text)]">$1B+</p>
            <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-2)]">
              Authenticated
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
